'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Users, MessageCircle, UserCheck, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Navbar } from '@/components/layout/Navbar';
import { useAuth } from '@/hooks/useAuth';
import { useDMStore } from '@/hooks/useDMNotifications';
import { api, Connection } from '@/lib/api';

export default function FriendsPage() {
  const router = useRouter();
  const { isAuthenticated, token } = useAuth();
  const [friends, setFriends] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const byFriend = useDMStore((s) => s.byFriend);

  useEffect(() => {
    if (!isAuthenticated) {
      router.replace('/login?redirect=/friends');
      return;
    }

    api.connections.getConnections(token ?? '')
      .then((data) => {
        const mutual = data.connections.filter((c) => c.isFriend);
        setFriends(mutual);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated, token, router]);

  const genderBadge = (gender: string | null) => {
    if (!gender) return null;
    const cfg: Record<string, string> = {
      MALE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      FEMALE: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      OTHER: 'bg-brand-surface-2 text-brand-gray border-white/10',
    };
    const label: Record<string, string> = { MALE: 'M', FEMALE: 'F', OTHER: '?' };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${cfg[gender] || cfg.OTHER}`}>
        {label[gender] || '?'}
      </span>
    );
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-brand-black pt-20 pb-20 md:pb-0">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link href="/chat">
              <button className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-brand-gray hover:text-brand-white hover:border-white/20 transition-colors">
                <ArrowLeft size={16} />
              </button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-brand-white">Friends</h1>
              <p className="text-sm text-brand-gray mt-0.5">Strangers you&apos;ve mutually added</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-brand-lime border-t-transparent rounded-full animate-spin" />
            </div>
          ) : friends.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-brand-surface-2 flex items-center justify-center">
                <Users size={28} className="text-brand-gray" />
              </div>
              <div>
                <p className="text-brand-white font-semibold">No friends yet</p>
                <p className="text-sm text-brand-gray mt-1">
                  Add strangers while chatting — both need accounts and must accept each other.
                </p>
              </div>
              <Link href="/chat">
                <Button size="sm">Start Chatting</Button>
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {friends.map((friend) => {
                const unread = byFriend[friend.userId] ?? 0;
                return (
                <div
                  key={friend.userId}
                  className="flex items-center justify-between px-4 py-3.5 rounded-2xl bg-brand-surface border border-white/[0.06] hover:border-white/10 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="relative w-10 h-10 rounded-full bg-brand-lime/10 border border-brand-lime/20 flex items-center justify-center shrink-0">
                      <span className="text-brand-lime font-bold text-sm">
                        {(friend.username?.[0] || '?').toUpperCase()}
                      </span>
                      {unread > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-lime text-brand-black text-[10px] font-bold flex items-center justify-center border-2 border-brand-black">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-semibold ${unread > 0 ? 'text-brand-white' : 'text-brand-white'}`}>{friend.username}</p>
                        {genderBadge(friend.gender)}
                        {friend.isVerified && (
                          <UserCheck size={12} className="text-brand-lime" />
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 ${unread > 0 ? 'text-brand-lime font-medium' : 'text-brand-gray'}`}>
                        {unread > 0
                          ? `${unread} new message${unread !== 1 ? 's' : ''}`
                          : `Mutual friend · ${friend.chatCount} chat${friend.chatCount !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  </div>

                  <Link href={`/messages/${friend.userId}`}>
                    <Button size="sm" variant={unread > 0 ? 'primary' : 'secondary'}>
                      <MessageCircle size={13} />
                      {unread > 0 ? 'Reply' : 'Message'}
                    </Button>
                  </Link>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
